import * as PIXI from "pixi.js";
import { Container, PointData, Sprite, Texture, Ticker, TilingSprite } from "pixi.js";
import { ITransform } from "./ITransform";
import { FilthTracker } from "./FilthTracker";
import { BeeType } from "./BeeType";
import { weightedSample } from "../random";
import { Game } from "./Game";
import { Geom } from "./Geom";
import { DebugShade } from "./DebugShade";
import { Bullet, createPooledBullet } from "./Bullet";
import { Item } from "./Item";
import { Hive } from "./Hive";
import { DamageTick } from "./DamageTick";
import { KeyIcon } from "./KeyIcon";
import { AbilityBee, ActiveBeeCategory, Bees, tickAbility } from "./Bees";

class BeeDisplay {
	public readonly container: PIXI.Container;
	// containers = [] as PIXI.Container[]
	private spriteCache = {} as Record<BeeType, Sprite>
	activeSprite?: Sprite | null;
	rotationSpeed = Math.random() > 0.5
		? (0.1 + Math.random() * 0.9) * Math.PI / 1000
		: (0.1 + Math.random() * 0.9) * -Math.PI / 1000;
	public targetSize: number = 0;
	public targetX: number = 0;
	public attackOffset: number = 0;
	public swerve = 0;
	public currentX = 0;

	constructor(private readonly player: Player, beeType: BeeType) {
		this.container = new Container();
	}

	showBee(beeType?: BeeType) {
		if (this.activeSprite) {
			this.activeSprite.visible = false
		}

		if (beeType == null) {
			this.activeSprite = null;
			return;
		}

		if (!this.spriteCache[beeType]) {
			this.spriteCache[beeType] = new Sprite(Bees[beeType].texture());
			this.spriteCache[beeType].anchor.x = 0.5;
			this.spriteCache[beeType].anchor.y = 0.5;
			this.targetSize = this.player.size;
			this.targetX = Math.random() * (this.targetSize - 8);
			this.currentX = this.targetX;
			this.spriteCache[beeType].x = this.currentX;
			this.container.addChild(this.spriteCache[beeType]);
		}

		this.activeSprite = this.spriteCache[beeType]
		this.activeSprite.visible = true
	}

	tick(deltaMS: number) {
		if (this.targetSize != this.player.size) {
			this.targetSize = this.player.size;
			this.targetX = Math.random() * (this.targetSize - 8);
		}
		if (this.activeSprite) {
			let distance = (this.targetX + this.attackOffset) - this.currentX;
			if (Math.abs(distance) > 0.5) {
				this.currentX += Math.sign(distance) * 0.5;
			}
			this.swerve += deltaMS;
			this.activeSprite.x = this.currentX + Math.sin(this.swerve / 500) * 4;
		}
		this.container.rotation += this.rotationSpeed * deltaMS;
	}
}

export class Player implements ITransform {

	public readonly container: PIXI.Container = new Container();
	public readonly shade: DebugShade = new DebugShade()
	public readonly filthTracker: FilthTracker;
	private takingDamageAnimationProgress: number = -1;
	public invincible: number = 0;
	public aimingReticle: Bullet;
	public highlightedItem: Item | Hive | null = null;
	public godmode = false;
	public noclip = false;
	public keyIcon: KeyIcon = new KeyIcon(this.container, { x: 0, y: -50 });

	public bees: Record<BeeType, number> = Object.fromEntries(
		Object.values(BeeType).map(type => [type, 0])
	) as any;

	public get beeCount(): number {
		let count = 0;
		for (const key of Object.values(BeeType)) {
			count += this.bees[key];
		}
		return count;
	}

	public get size(): number {
		if (this.beeCount < 10) {
			return Geom.remap(0, 10, this.beeCount, 10, 20);
		} else if (this.beeCount < 20) {
			return Geom.remap(10, 20, this.beeCount, 20, 30);
		} else if (this.beeCount < 50) {
			return Geom.remap(20, 50, this.beeCount, 30, 50);
		} else {
			return 50;
		}
	}

	public get speed(): number {
		let modifier = 1;
		if (this.beeCount < 5) {
			modifier = 1.3;
		} else if (this.beeCount < 15) {
			modifier = Geom.remap(5, 15, this.beeCount, 1.3, 1.0);
		} else if (this.beeCount < 25) {
			modifier = Geom.remap(15, 25, this.beeCount, 1.0, 1.0);
		} else if (this.beeCount < 50) {
			modifier = Geom.remap(25, 50, this.beeCount, 1.0, 0.1);
		} else {
			modifier = 0.1;
		}

		return 0.28 * modifier;
	}

	beeDisplays = [] as BeeDisplay[]

	public constructor(
		parent: Container,
		filthTracker: FilthTracker
	) {
		this.filthTracker = new FilthTracker(filthTracker);
		this.shade.size = this.size
		this.container.addChild(this.shade.graphics);
		this.container.zIndex = 1;
		parent.addChild(this.container)
		this.aimingReticle = new Bullet(this.container);
		this.aimingReticle.x = 30;
		this.aimingReticle.size = 3;
		this.reset();
	}

	public reset() {
		for (const type of Object.values(BeeType)) {
			this.bees[type] = 0;
		}
		this.addBee(BeeType.DiesFirstBee)
		// this.addBee(BeeType.DashBee)
		// this.addBee(BeeType.BulletBee)
	}

	public fireBullet(game: Game, damage: number, direction?: PointData): void {
		if (!direction) {
			return;
		}

		const bullet = createPooledBullet(game.camera.container,
			this.x + direction.x * 30,
			this.y + direction.y * 30,
			Geom.angle({ x: 0, y: 0 }, direction),
			3,
			true,
			damage
		)
		game.bullets.push(bullet);
	}

	public aimReticle(direction: PointData): void {
		const reticleLocation = Geom.multiply(Geom.normalize(direction), 30);
		this.aimingReticle.container.x = reticleLocation.x
		this.aimingReticle.container.y = reticleLocation.y
	}

	public tick(game: Game, deltaMS: number) {
		if (this.keyIcon.container.visible) this.keyIcon.tick(deltaMS);
		for (const type of Object.values(BeeType)) {
			if (this.bees[type] > 0) tickAbility(this.bees[type], Bees[type], game, deltaMS);
		}

		for (const display of this.beeDisplays) {
			display.tick(deltaMS);
		}

		let [deltaX, deltaY] = [0, 0];
		if (game.keys.left.held) deltaX = -1;
		if (game.keys.right.held) deltaX = 1;
		if (game.keys.up.held) deltaY = -1;
		if (game.keys.down.held) deltaY = 1;
		const length = Math.sqrt(deltaX ** 2 + deltaY ** 2);
		if (length > 0) {
			deltaX = deltaX / length;
			deltaY = deltaY / length;
		}
		const speed = this.speed;
		this.container.x += deltaX * speed * deltaMS;
		this.container.y += deltaY * speed * deltaMS;
		if (!this.noclip) {
			if (this.container.x - this.size < game.ground.minX) {
				this.container.x = game.ground.minX + this.size;
			}
			if (this.container.x + this.size > game.ground.maxX) {
				this.container.x = game.ground.maxX - this.size;
			}
			if (this.container.y - this.size < game.ground.minY) {
				this.container.y = game.ground.minY + this.size;
			}
			if (this.container.y + this.size > game.ground.maxY) {
				this.container.y = game.ground.maxY - this.size;
			}
		}

		const lastHighlighted = this.highlightedItem;
		if (this.highlightedItem?.destroyed) this.highlightedItem = null;
		for (const item of game.items) this.processShouldHighlight(item);
		for (const hive of game.hives) this.processShouldHighlight(hive);
		if (this.highlightedItem != null && Geom.distance(this, this.highlightedItem) > this.size + this.highlightedItem.size) {
			this.highlightedItem = null;
		}
		if (lastHighlighted != this.highlightedItem) {
			lastHighlighted?.unhighlight();
			this.highlightedItem?.highlight();
			this.keyIcon.container.visible = this.highlightedItem != null && !game.keys.e.pressed;
			this.keyIcon.description.visible = this.highlightedItem instanceof Hive;
		}
		if (game.keys.e.pressed || game.keys.e.released) {
			this.keyIcon.container.visible = this.highlightedItem != null && !game.keys.e.pressed;
			this.keyIcon.description.visible = this.highlightedItem instanceof Hive;
		}

		this.shade.size = this.size;

		if (this.takingDamageAnimationProgress >= 0) {
			this.takingDamageAnimationProgress += deltaMS;
			this.container.tint = {
				r: 255,
				g: this.takingDamageAnimationProgress / 150 * 255,
				b: this.takingDamageAnimationProgress / 150 * 255
			};
			if (this.takingDamageAnimationProgress >= 150) {
				this.takingDamageAnimationProgress = -1;
				this.container.tint = 0xFFFFFF;
			}
		}
	}

	public processShouldHighlight(item: Item | Hive) {
		let distance = Geom.distance(item, this);
		if (distance < this.size + item.size) {
			if (this.highlightedItem == null || distance < Geom.distance(this, this.highlightedItem)) {
				this.highlightedItem = item;
			}
		}
	}

	public takeDamage(game: Game, amount: number): boolean {
		if (this.godmode) return false;

		if (this.invincible > 0) {
			return false;
		}

		game.texts.push(new DamageTick(game.camera.container, this, amount))
		while (amount > 0 && this.beeCount > 0) {
			// Find highest layer.
			let layer = -1;
			for (const beeType of Object.values(BeeType)) {
				if (this.bees[beeType] > 0 && (Bees[beeType].deathLayer ?? 0) > layer) {
					layer = Bees[beeType].deathLayer ?? 0;
				}
			}
			// Use weighting to select who dies first from this layer.
			const weightedBees: [number, BeeType][] = [];
			for (const beeType of Object.values(BeeType)) {
				if (this.bees[beeType] > 0 && (Bees[beeType].deathLayer ?? 0) == layer) {
					weightedBees.push([(Bees[beeType].deathWeighting ?? 1) * this.bees[beeType], beeType])
				}
			}
			let beeType = weightedSample(weightedBees);
			if (this.bees.BasicBee > 0) beeType = BeeType.BasicBee;
			if (--this.bees[beeType] === 0) {
				Bees[beeType].onLost?.(this);
			}
			amount--;
			this.beeDisplays[this.beeCount].showBee()
			this.takingDamageAnimationProgress = 0;
		}
		if (this.highlightedItem instanceof Hive) {
			this.highlightedItem.charging = 0;
		}

		this.filthTracker.soil()
		this.takingDamageAnimationProgress = 0;
		return true;
	}

	public addBee(bee: BeeType) {
		if (this.bees[bee] === 0) {
			Bees[bee].onAcquired?.(this);
		}

		this.bees[bee]++;
		if (this.beeDisplays.length < this.beeCount) {
			const display = new BeeDisplay(this, bee)
			this.container.addChild(display.container)
			this.beeDisplays.push(display)
			display.showBee(bee)
		} else {
			this.beeDisplays[this.beeCount - 1].showBee(bee)
		}

		this.filthTracker.soil();
	}

	public destroy() {
		// this.sprite.parent.removeFromParent();
	}

	// region ITransform

	public get x(): number {
		return this.container.x;
	}

	public set x(value: number) {
		this.container.x = value;
	}

	public get y(): number {
		return this.container.y;
	}

	public set y(value: number) {
		this.container.y = value;
	}

	public get w(): number {
		return this.container.width;
	}

	public get h(): number {
		return this.container.height;
	}

	// endregion
}
